import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { AnswerPill, LabelPill, StatusPill, fmtTs } from "./sumsubShared";

export function SumsubList({ data, loading, offset, limit, onPage, onOpen }) {
  const items = data?.items || [];
  const total = data?.total || 0;
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="sumsub-list-panel">
      <div className="overflow-auto max-h-[calc(100vh-21rem)]">
        <table className="w-full text-sm" data-testid="sumsub-table">
          <thead className="sticky top-0 z-10 border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Applicant</th>
              <th className="px-3 py-2 text-left font-semibold">Company / NI</th>
              <th className="px-3 py-2 text-left font-semibold">Level</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th>
              <th className="px-3 py-2 text-left font-semibold">Result</th>
              <th className="px-3 py-2 text-left font-semibold">Risk</th>
              <th className="px-3 py-2 text-left font-semibold">Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.applicant_id} className="group cursor-pointer border-b hover:bg-slate-50" onClick={() => onOpen(a.applicant_id)} data-testid={`sumsub-row-${a.applicant_id}`}>
                <td className="px-3 py-2 align-top">
                  <div className="font-medium leading-tight text-slate-900" data-testid={`sumsub-row-name-${a.applicant_id}`}>{a.full_name || <span className="italic text-slate-400">No name yet</span>}</div>
                  <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{[a.email, a.phone].filter(Boolean).join(" · ") || a.external_user_id}</div>
                </td>
                <td className="px-3 py-2 align-top text-xs text-slate-600">
                  <div>{a.custom_fields?.["Company Name"] || "—"}</div>
                  {a.custom_fields?.["National Insurance Number"] && <div className="font-mono text-[11px] text-slate-400">{a.custom_fields["National Insurance Number"]}</div>}
                </td>
                <td className="px-3 py-2 align-top text-xs text-slate-600">{a.level_name || "—"}</td>
                <td className="px-3 py-2 align-top"><StatusPill status={a.review_status} /></td>
                <td className="px-3 py-2 align-top">
                  <div className="flex flex-wrap items-center gap-1">
                    <AnswerPill answer={a.review_answer} />
                    {a.reject_type && <LabelPill tone={a.reject_type === "FINAL" ? "red" : "amber"}>{a.reject_type}</LabelPill>}
                    {(a.reject_labels || []).slice(0, 2).map((l) => <LabelPill key={l} tone="red">{l}</LabelPill>)}
                    {a.reject_labels?.length > 2 && <LabelPill>+{a.reject_labels.length - 2}</LabelPill>}
                  </div>
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="flex flex-wrap gap-1">{(a.risk_labels || []).map((r) => <LabelPill key={r} tone="amber">{r.split(":")[1] || r}</LabelPill>)}</div>
                </td>
                <td className="px-3 py-2 align-top font-mono text-[11px] text-slate-500 whitespace-nowrap">{fmtTs(a.created_at)}</td>
                <td className="px-2 py-2 align-top text-right"><ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100" /></td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-400" data-testid="sumsub-empty">{loading ? "Loading applicants…" : "No applicants match these filters"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
        <span data-testid="sumsub-pagination-info">{total ? `${offset + 1}–${Math.min(offset + limit, total)} of ${total}` : "0 applicants"}</span>
        <span className="flex items-center gap-1">
          <a href="https://cockpit.sumsub.com" target="_blank" rel="noreferrer" className="mr-3 inline-flex items-center gap-1 text-slate-400 hover:text-slate-700" data-testid="sumsub-open-cockpit">Open Cockpit <ExternalLink className="w-3 h-3" /></a>
          <Button variant="outline" size="sm" className="h-7 px-2" disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - limit))} data-testid="sumsub-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
          <Button variant="outline" size="sm" className="h-7 px-2" disabled={offset + limit >= total} onClick={() => onPage(offset + limit)} data-testid="sumsub-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
        </span>
      </div>
    </div>
  );
}
